import * as React from 'react'

import {
  ColumnDef,
  getCoreRowModel,
  Row,
  SortingState,
  Updater,
  useReactTable,
} from '@tanstack/react-table'
import { Info } from 'lucide-react'
import { observer } from 'mobx-react'

import {
  ILocationClosureInterval,
  IProjectClosedInterval,
  IWorkflowLocationRestriction,
  RestrictionType,
} from '~/client/graph'
import { buildGroupedRows } from '~/client/src/desktop/components/Resource/buildGroupedRows'
import {
  IResourceColumn,
  IResourceRecord,
} from '~/client/src/desktop/components/Resource/types'
import { ICompanyMetadata } from '~/client/src/shared/components/CompanyListSelector'
import { ResourceGroupHeader } from '~/client/src/shared/components/GroupHeader'
import HoursOfOperationCell from '~/client/src/shared/components/ResourceControls/cells/HoursOfOperationCell'
import PermittedCompaniesCell from '~/client/src/shared/components/ResourceControls/cells/PermittedCompaniesCell'
import WorkflowRestrictionCell from '~/client/src/shared/components/ResourceControls/cells/WorkflowRestrictionCell'
import WorkflowTypesCell from '~/client/src/shared/components/ResourceControls/cells/WorkflowTypesCell'
import {
  isControlDeniedForRecord,
  isProjectLocationRecord,
} from '~/client/src/shared/components/ResourceControls/controlKeys'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import { SharedVirtualTable } from '~/client/src/shared/components/SharedVirtualTable'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import type { IWorkflowRestrictionDraft } from '~/client/src/shared/resources/domain/resourceControls'
import { useResolveGroupPresentation } from '~/client/src/shared/resources/presentation/resolveGroupPresentation'
import { IResourceFieldProjector } from '~/client/src/shared/resources/projection/types'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import { IHierarchyAccessors } from '~/client/src/shared/utils/hierarchyAccessors'

import DeleteResourceCell from './cells/DeleteResourceCell'
import ShowInMapCell from './cells/ShowInMapCell'
import StoreBackedDateClosuresCell from './cells/StoreBackedDateClosuresCell'
import WorkflowResourceCell from './cells/WorkflowResourceCell'
import { getControlsGroupPaths } from './getControlsGroupPaths'
import {
  DATE_CLOSURES_KEY,
  DELETE_COLUMN_KEY,
  HOURS_OF_OPERATION_KEY,
  MAX_BOOKING_DURATION_KEY,
  MAX_OVERLAPPING_KEY,
  PERMITTED_COMPANIES_KEY,
  SELECT_COLUMN_KEY,
  SHOW_IN_MAP_KEY,
  WORKFLOW_RESOURCE_KEY,
  WORKFLOW_TYPES_KEY,
} from './resourceControlsColumns'
import { IResourceControlsLabels } from './types'

// Only ID and Name have a defined sort order (the array/interval columns don't).
// Their headers become click-to-sort; everything else stays static.
const SORTABLE_COLUMN_KEYS = new Set(['stxResourceId', 'name'])
const GROUP_ROW_HEIGHT = 32
const RECORD_ROW_HEIGHT = 44

interface IControlHeaderProps {
  label: string
  tooltip: string
}

/** Toggle-control header: an info icon carrying the on/off helper text. */
function ControlHeaderWithTooltip({ label, tooltip }: IControlHeaderProps) {
  return (
    <span className="tw:inline-flex tw:items-center tw:gap-1">
      <Tooltip>
        <TooltipTrigger
          type="button"
          aria-label={tooltip}
          className="tw:inline-flex tw:cursor-default tw:items-center tw:text-muted-foreground tw:hover:text-foreground"
        >
          <Info className="tw:size-3.5" />
        </TooltipTrigger>
        <TooltipContent className="tw:max-w-[280px] tw:whitespace-pre-line">
          {tooltip}
        </TooltipContent>
      </Tooltip>
      {label}
    </span>
  )
}

interface IControlsRow {
  id: string
  rowType: 'record' | 'group'
  record?: IResourceRecord
  groupKey?: string
  groupLabel?: string
  groupRecordIds?: string[]
  groupPath?: string
  depth?: number
  isGroupCollapsed?: boolean
}

interface IProps {
  records: IResourceRecord[]
  columns: IResourceColumn[]
  /** The project's own closures, mirrored onto the site root's Date Closures. */
  projectClosedIntervals?: IProjectClosedInterval[]
  grouping: string | null
  groupingHierarchy?: IHierarchyAccessors
  /** Needed alongside `grouping` for a field to contribute group artwork. */
  resourceTypeId?: string
  collapsedCategories: Map<string, boolean>
  toggleCategory: (categoryId: string) => void
  companies: Company[]
  companyMetadata?: Map<string, ICompanyMetadata>
  getCompanyById: (id: string) => Company | undefined
  getPermitTypeById: (id: string) => WorkflowType | undefined
  /** Every type; what an "All" ([]) control expands to when grouping. */
  allWorkflowTypeIds: string[]
  selectedSortingOptionId: string | null
  selectedSortOrder: SortOrder
  onSortingChange: (optionId: string, order: SortOrder) => void
  selectedIds: Set<string>
  onToggleSelection: (recordId: string) => void
  onSetSelection: (ids: Set<string>) => void
  onSelectRecord: (recordId: string) => void
  onDeleteRecord: (recordId: string) => void | Promise<void>
  onSavePermittedCompanies: (recordId: string, companyIds: string[]) => void
  /** Turns WR on for a record an edit has just assigned something to. */
  onEnableBookable: (recordId: string) => void
  onSaveBookable: (
    recordId: string,
    isBookable: boolean,
  ) => void | Promise<void>
  onSaveShowInMap: (
    recordId: string,
    showInMap: boolean,
  ) => void | Promise<void>
  onSaveWorkflowRestrictions: (
    recordId: string,
    drafts: IWorkflowRestrictionDraft[],
  ) => void
  onDeleteWorkflowRestrictions: (recordId: string, ids: string[]) => void
  onSaveOperatingIntervals: (
    recordId: string,
    intervals: IMsDateInterval[],
  ) => void
  /** Navigates to the project settings Time tab; drives the inherited-hours chip link. */
  onEditProjectHours?: () => void
  closuresStore: ClosuresStore
  projectDateStore: ProjectDateStore
  onSaveClosure: (
    recordId: string,
    closureInterval: ILocationClosureInterval,
    closureId?: string,
  ) => void
  onRemoveClosure: (closureId: string) => void
  confirmDelete: IResourceControlsLabels['confirmDelete']
  ariaLabel: string
  fieldProjector: IResourceFieldProjector
  /** The controls this resource exposes — gates the Show in Maps column. */
  controls: readonly string[]
}

function ResourceControlsTable({
  records,
  columns,
  projectClosedIntervals,
  grouping,
  groupingHierarchy,
  resourceTypeId,
  collapsedCategories,
  toggleCategory,
  companies,
  companyMetadata,
  getCompanyById,
  getPermitTypeById,
  allWorkflowTypeIds,
  selectedSortingOptionId,
  selectedSortOrder,
  onSortingChange,
  selectedIds,
  onToggleSelection,
  onSetSelection,
  onSelectRecord,
  onDeleteRecord,
  onSavePermittedCompanies,
  onEnableBookable,
  onSaveBookable,
  onSaveShowInMap,
  onSaveWorkflowRestrictions,
  onDeleteWorkflowRestrictions,
  onSaveOperatingIntervals,
  onEditProjectHours,
  closuresStore,
  projectDateStore,
  onSaveClosure,
  onRemoveClosure,
  confirmDelete,
  ariaLabel,
  fieldProjector,
  controls,
}: IProps) {
  // Read selection through a ref inside the column cell/header renderers so the
  // columnDefs memo isn't keyed on selectedIds — otherwise every checkbox toggle
  // rebuilds all column defs. The renderers re-run on each table render (the host
  // re-renders on selection change), reading the current value off the ref.
  const selectedIdsRef = React.useRef(selectedIds)
  selectedIdsRef.current = selectedIds

  const rows = React.useMemo<IControlsRow[]>(() => {
    if (!grouping) {
      return records.map(record => ({
        id: record.id,
        rowType: 'record',
        record,
      }))
    }

    const groupDeps = {
      fieldProjector,
      getCompanyName: (id: string) => getCompanyById(id)?.name,
      getWorkflowTypeName: (id: string) => getPermitTypeById(id)?.name,
      allCompanyIds: companies.map(company => company.id),
      allWorkflowTypeIds,
    }

    return buildGroupedRows<IResourceRecord, IControlsRow>({
      records,
      fieldKey: grouping,
      getRecordGroupPaths: record =>
        getControlsGroupPaths(record, grouping, groupDeps, groupingHierarchy),
      getRecordId: record => record.id,
      collapsed: collapsedCategories,
      buildGroupRow: (group, rowId) => ({
        id: rowId,
        rowType: 'group',
        groupKey: group.value,
        groupLabel: group.label,
        groupRecordIds: group.recordIds,
        groupPath: group.path,
        depth: group.depth,
        isGroupCollapsed: group.isCollapsed,
      }),
      buildLeafRow: (record, rowId) => ({
        id: rowId,
        rowType: 'record',
        record,
      }),
    })
  }, [
    records,
    grouping,
    groupingHierarchy,
    collapsedCategories,
    companies,
    allWorkflowTypeIds,
    getCompanyById,
    getPermitTypeById,
    fieldProjector,
  ])

  const columnDefs = React.useMemo<ColumnDef<IControlsRow>[]>(() => {
    const selectableIds = records.map(record => record.id)

    const selectColumn: ColumnDef<IControlsRow> = {
      id: SELECT_COLUMN_KEY,
      header: () => {
        const selected = selectedIdsRef.current
        const allSelected =
          selectableIds.length > 0 &&
          selectableIds.every(id => selected.has(id))
        const someSelected =
          !allSelected && selectableIds.some(id => selected.has(id))
        return (
          <div className="tw:flex tw:h-full tw:w-full tw:items-center tw:justify-center">
            <Checkbox
              aria-label={Localization.translator.selectAll}
              checked={allSelected}
              indeterminate={someSelected}
              onCheckedChange={() =>
                onSetSelection(allSelected ? new Set() : new Set(selectableIds))
              }
            />
          </div>
        )
      },
      cell: ({ row }) => {
        const record = row.original.record
        if (!record) return null
        return (
          <div
            className="tw:flex tw:w-full tw:items-center tw:justify-center"
            onClick={event => event.stopPropagation()}
          >
            <Checkbox
              aria-label={Localization.translator.select}
              checked={selectedIdsRef.current.has(record.id)}
              onCheckedChange={() => onToggleSelection(record.id)}
            />
          </div>
        )
      },
      // The group band's left padding is derived from this width; keep them in sync.
      size: 40,
      enableSorting: false,
    }

    const dataColumns = columns.map(column => ({
      id: column.dataKey,
      // TanStack's getCanSort() requires an accessorFn; the value is unused for
      // rendering (cell overrides) and sorting (manualSorting), but without it
      // the header never becomes click-sortable.
      accessorFn: (row: IControlsRow) =>
        row.record ? column.accessorFn(row.record) : undefined,
      header: column.label ?? '',
      cell: ({ row }) => {
        const record = row.original.record
        if (!record) return null

        // The WR toggle gates every control column's display: off => "Not Set".
        const isBookable = record.root?.isBookable ?? false

        // A control its kind forbids outright, as opposed to merely unset: the column stays
        // for every other location and only this row reads "Not permitted". Placed after the
        // Id/Name branch below would read as if those could be denied too, so it sits here
        // and names the control keys it can match.
        if (isControlDeniedForRecord(column.dataKey, resourceTypeId, record)) {
          return (
            <span className="tw:text-body-md tw:text-muted-foreground tw:leading-[20px]">
              {Localization.translator.notPermitted}
            </span>
          )
        }

        if (column.dataKey === 'stxResourceId' || column.dataKey === 'name') {
          const value = column.accessorFn(record)
          // Fill the whole cell so a click anywhere in it opens the panel.
          // tw:absolute tw:inset-0 spans the BodyCell's padding box; re-apply
          // tw:px-3 so the text keeps the same position as other cells.
          return (
            <div
              className="tw:absolute tw:inset-0 tw:flex tw:cursor-pointer tw:items-center tw:px-3"
              onClick={() => onSelectRecord(record.id)}
            >
              <span className="tw:truncate">{String(value)}</span>
            </div>
          )
        }

        if (column.dataKey === WORKFLOW_TYPES_KEY) {
          return (
            <WorkflowTypesCell
              isBookable={isBookable}
              onAssign={onEnableBookable}
              resourceTypeId={resourceTypeId}
              recordId={record.id}
            />
          )
        }

        if (column.dataKey === PERMITTED_COMPANIES_KEY) {
          return (
            <PermittedCompaniesCell
              companyIds={record.root?.permittedCompanies ?? []}
              companies={companies}
              companyMetadata={companyMetadata}
              isBookable={isBookable}
              getCompanyById={getCompanyById}
              onChange={ids => onSavePermittedCompanies(record.id, ids)}
            />
          )
        }

        if (
          column.dataKey === MAX_BOOKING_DURATION_KEY ||
          column.dataKey === MAX_OVERLAPPING_KEY
        ) {
          const isOverlapping = column.dataKey === MAX_OVERLAPPING_KEY
          const restrictions = column.accessorFn(
            record,
          ) as IWorkflowLocationRestriction[]
          return (
            <WorkflowRestrictionCell
              recordId={record.id}
              restrictions={restrictions}
              restrictionType={
                isOverlapping
                  ? RestrictionType.MaxOverlapping
                  : RestrictionType.MaxDuration
              }
              // Not the column header: its "(hours)" would double the unit the
              // editor already names on a duration value.
              label={
                isOverlapping
                  ? Localization.translator.maxOverlapping
                  : Localization.translator.maxBookingDuration
              }
              allWorkflowTypeIds={allWorkflowTypeIds}
              isBookable={isBookable}
              getPermitTypeById={getPermitTypeById}
              onSave={onSaveWorkflowRestrictions}
              onDelete={ids => onDeleteWorkflowRestrictions(record.id, ids)}
            />
          )
        }

        if (column.dataKey === HOURS_OF_OPERATION_KEY) {
          const intervals = column.accessorFn(record) as IMsDateInterval[]
          return (
            <HoursOfOperationCell
              intervals={intervals}
              projectDateStore={projectDateStore}
              isBookable={isBookable}
              onChange={intervals =>
                onSaveOperatingIntervals(record.id, intervals)
              }
              onEditProjectHours={onEditProjectHours}
            />
          )
        }

        if (column.dataKey === DATE_CLOSURES_KEY) {
          return (
            <StoreBackedDateClosuresCell
              recordId={record.id}
              closuresStore={closuresStore}
              projectDateStore={projectDateStore}
              isBookable={isBookable}
              onSave={(closureInterval, closureId) =>
                onSaveClosure(record.id, closureInterval, closureId)
              }
              onRemove={onRemoveClosure}
              // Only the site root inherits: the project's closures already apply
              // project-wide on the backend, so this states a rule in force rather than
              // offering a new one, and it is the root that stands for the project.
              inheritedClosures={
                isProjectLocationRecord(resourceTypeId, record)
                  ? projectClosedIntervals
                  : undefined
              }
              onEditProjectClosures={onEditProjectHours}
            />
          )
        }

        const value = column.accessorFn(record)
        return value != null ? (
          <span className="tw:truncate">{String(value)}</span>
        ) : null
      },
      size: column.width ?? 200,
      enableSorting: SORTABLE_COLUMN_KEYS.has(column.dataKey),
    }))

    const workflowResourceColumn: ColumnDef<IControlsRow> = {
      id: WORKFLOW_RESOURCE_KEY,
      header: () => (
        <ControlHeaderWithTooltip
          label={Localization.translator.workflowResource}
          tooltip={Localization.translator.workflowResourceTooltip}
        />
      ),
      cell: ({ row }) => {
        const record = row.original.record
        if (!record) return null
        return (
          <WorkflowResourceCell
            isBookable={record.root?.isBookable ?? false}
            onToggle={value => onSaveBookable(record.id, value)}
          />
        )
      },
      size: 80,
      enableSorting: false,
    }

    // Independent of the WR gate — always interactive, defaults to shown.
    const showInMapColumn: ColumnDef<IControlsRow> = {
      id: SHOW_IN_MAP_KEY,
      header: () => (
        <ControlHeaderWithTooltip
          label={Localization.translator.showInMap}
          tooltip={Localization.translator.showInMapTooltip}
        />
      ),
      cell: ({ row }) => {
        const record = row.original.record
        if (!record) return null
        return (
          <ShowInMapCell
            showInMap={record.root?.showInMap ?? true}
            onToggle={value => onSaveShowInMap(record.id, value)}
          />
        )
      },
      size: 140,
      enableSorting: false,
    }

    const deleteColumn: ColumnDef<IControlsRow> = {
      id: DELETE_COLUMN_KEY,
      header: '',
      cell: ({ row }) => {
        const record = row.original.record
        if (!record) return null
        return (
          <div className="tw:flex tw:w-full tw:items-center tw:justify-center">
            <DeleteResourceCell
              onDelete={() => onDeleteRecord(record.id)}
              confirmDelete={confirmDelete}
              deniedReason={
                isProjectLocationRecord(resourceTypeId, record)
                  ? Localization.translator.projectLocationDeletionProhibited
                  : undefined
              }
            />
          </div>
        )
      },
      size: 64,
      enableSorting: false,
    }

    return [
      selectColumn,
      ...dataColumns,
      ...(controls.includes(SHOW_IN_MAP_KEY) ? [showInMapColumn] : []),
      workflowResourceColumn,
      deleteColumn,
    ]
  }, [
    controls,
    columns,
    records,
    onToggleSelection,
    onSetSelection,
    companies,
    companyMetadata,
    getCompanyById,
    getPermitTypeById,
    allWorkflowTypeIds,
    onSelectRecord,
    onDeleteRecord,
    onSavePermittedCompanies,
    onEnableBookable,
    onSaveBookable,
    onSaveShowInMap,
    onSaveWorkflowRestrictions,
    onDeleteWorkflowRestrictions,
    onSaveOperatingIntervals,
    onEditProjectHours,
    closuresStore,
    projectDateStore,
    onSaveClosure,
    onRemoveClosure,
    confirmDelete,
    resourceTypeId,
    projectClosedIntervals,
  ])

  // Bridge header-click sorting to the same group/sort state the toolbar drives.
  // Data is sorted externally (applySorting), so sorting is manual here.
  const sortingState = React.useMemo<SortingState>(
    () =>
      selectedSortingOptionId
        ? [
            {
              id: selectedSortingOptionId,
              desc: selectedSortOrder === SortOrder.DESC,
            },
          ]
        : [],
    [selectedSortingOptionId, selectedSortOrder],
  )

  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sortingState) : updater
    if (next.length > 0) {
      const { id, desc } = next[0]
      onSortingChange(id, desc ? SortOrder.DESC : SortOrder.ASC)
    } else {
      onSortingChange('', SortOrder.DEFAULT)
    }
  }

  // Checkbox always pinned left; Show in Maps, WR and delete always pinned right;
  // every other column is pinned left only when it's fixed in the Display settings.
  const columnPinning = React.useMemo(
    () => ({
      left: [
        SELECT_COLUMN_KEY,
        ...columns
          .filter(column => column.isFixed)
          .map(column => column.dataKey),
      ],
      right: [
        ...(controls.includes(SHOW_IN_MAP_KEY) ? [SHOW_IN_MAP_KEY] : []),
        WORKFLOW_RESOURCE_KEY,
        DELETE_COLUMN_KEY,
      ],
    }),
    [columns, controls],
  )

  const resolveGroupPresentation = useResolveGroupPresentation()

  const table = useReactTable<IControlsRow>({
    data: rows,
    columns: columnDefs,
    getRowId: row => row.id,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    onSortingChange: handleSortingChange,
    state: { sorting: sortingState, columnPinning },
  })

  const renderRow = React.useCallback(
    (row: Row<IControlsRow>, defaultCells: React.ReactNode[]) => {
      if (row.original.rowType !== 'group') return defaultCells

      const groupPath = row.original.groupPath || ''
      const isCollapsed = row.original.isGroupCollapsed === true

      // Select-all for the group: checked when every record in it is selected,
      // indeterminate when some are. Toggling adds/removes the whole group.
      const groupRecordIds = row.original.groupRecordIds ?? []
      const selectedInGroup = groupRecordIds.filter(id =>
        selectedIds.has(id),
      ).length
      const allInGroupSelected =
        groupRecordIds.length > 0 && selectedInGroup === groupRecordIds.length
      const someInGroupSelected = selectedInGroup > 0 && !allInGroupSelected
      const onGroupSelectToggle = () => {
        const next = new Set(selectedIds)
        groupRecordIds.forEach(id =>
          allInGroupSelected ? next.delete(id) : next.add(id),
        )
        onSetSelection(next)
      }

      return (
        <div
          role="gridcell"
          aria-colindex={1}
          aria-colspan={table.getVisibleLeafColumns().length}
          className="tw:relative tw:flex tw:items-center tw:border-0 tw:border-b tw:border-solid tw:border-input-soft tw:bg-sidebar tw:select-none"
          style={{
            minHeight: GROUP_ROW_HEIGHT,
            width: table.getTotalSize(),
            minWidth: table.getTotalSize(),
          }}
          onClick={event => {
            // The header band owns its own toggle (its trigger button calls
            // onToggle). This handler only fires for clicks in the gridcell's
            // empty space beyond the sticky-left band — otherwise the band click
            // would bubble here and toggle a second time (net no-op).
            const target = event.target as HTMLElement
            if (target.closest('[data-slot="resource-group-band"]')) return
            toggleCategory(groupPath)
          }}
        >
          <ResourceGroupHeader
            label={
              row.original.groupLabel || row.original.groupKey || groupPath
            }
            presentation={resolveGroupPresentation({
              propertyConfig: undefined,
              groupKey: row.original.groupKey || '',
              count: row.original.groupRecordIds?.length,
              propertyName: grouping,
              resourceTypeId,
            })}
            count={row.original.groupRecordIds?.length}
            isExpanded={!isCollapsed}
            onToggle={() => toggleCategory(groupPath)}
            depth={row.original.depth}
            selection={{
              checked: allInGroupSelected,
              indeterminate: someInGroupSelected,
              onChange: onGroupSelectToggle,
              ariaLabel: Localization.translator.selectAll,
            }}
            // pl: row checkbox is centred in the 40px column's px-3 content box (15px after the
            // 1px border-r) → 12 + (15 - 16) / 2.
            className="tw:sticky tw:left-0 tw:z-[1] tw:w-fit tw:max-w-full tw:border-b-0 tw:pl-[11.5px]"
          />
        </div>
      )
    },
    [
      toggleCategory,
      table,
      selectedIds,
      onSetSelection,
      grouping,
      resourceTypeId,
      resolveGroupPresentation,
    ],
  )

  return (
    <SharedVirtualTable
      table={table}
      ariaLabel={ariaLabel}
      renderRow={renderRow}
      estimateRowHeight={row =>
        row.original.rowType === 'group' ? GROUP_ROW_HEIGHT : RECORD_ROW_HEIGHT
      }
      getRowClassName={row =>
        row.original.rowType === 'group'
          ? 'tw:bg-sidebar tw:cursor-pointer'
          : undefined
      }
    />
  )
}

export default observer(ResourceControlsTable)
